import React, { useState, useRef, useEffect } from 'react'
import { Zap, Target, RotateCcw, CheckCircle, Info } from 'lucide-react'
import { gsap } from 'gsap'

interface InteractiveVelocityExplorationProps {
  data: {
    title: string
    instruction: string
    interactive_type: string
    simulation_config: any
    exploration_tasks: Array<{
      task: string
      expected_observation: string
      explanation: string
    }>
    key_concept: string
  }
  onComplete: () => void
}

const InteractiveVelocityExploration: React.FC<InteractiveVelocityExplorationProps> = ({ data, onComplete }) => {
  const [currentTask, setCurrentTask] = useState(0)
  const [hasCompletedTask, setHasCompletedTask] = useState(false)
  const [showExplanation, setShowExplanation] = useState(false)
  
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current) {
      gsap.fromTo(containerRef.current, 
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }
      )
    }
  }, [])

  const handleTaskComplete = () => {
    setHasCompletedTask(true)
    setTimeout(() => {
      if (currentTask < data.exploration_tasks.length - 1) {
        setCurrentTask(currentTask + 1)
        setHasCompletedTask(false)
      } else {
        onComplete()
      }
    }, 2000)
  }

  const currentTaskData = data.exploration_tasks[currentTask]

  return (
    <div ref={containerRef} className="brilliant-card p-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-dark-900 mb-4">
          {data.title}
        </h2>
        <div className="w-24 h-1 bg-gradient-brilliant mx-auto rounded-full"></div>
      </div>

      {/* Instruction */}
      <div className="bg-gradient-to-r from-secondary-50 to-blue-50 p-6 rounded-2xl border border-secondary-200 mb-8">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-2xl flex items-center justify-center flex-shrink-0">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-dark-900 mb-3">
              Hướng dẫn
            </h3>
            <p className="text-dark-700 text-lg leading-relaxed">
              {data.instruction}
            </p>
          </div>
        </div>
      </div>

      {/* Current Task */}
      <div className="bg-gradient-to-r from-primary-50 to-primary-100 p-6 rounded-2xl border border-primary-200 mb-6">
        <h4 className="font-bold text-primary-800 mb-4 flex items-center">
          <Target className="w-5 h-5 mr-2" />
          Nhiệm vụ {currentTask + 1}/{data.exploration_tasks.length}:
        </h4>
        <p className="text-primary-700 text-lg mb-6">
          {currentTaskData.task}
        </p>
        
        <button
          onClick={handleTaskComplete}
          className="px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold text-lg rounded-xl hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-2xl"
        >
          ✓ Hoàn thành nhiệm vụ
        </button>
      </div>

      {/* Task Feedback */}
      {hasCompletedTask && (
        <div className="bg-gradient-to-r from-accent-green/10 to-green-50 p-6 rounded-2xl border border-green-300 mb-6">
          <div className="flex items-center space-x-4">
            <CheckCircle className="w-8 h-8 text-accent-green flex-shrink-0" />
            <div>
              <h5 className="font-bold text-green-800 mb-2">Nhiệm vụ hoàn thành!</h5>
              <p className="text-green-700">
                <strong>Quan sát:</strong> {currentTaskData.expected_observation}
              </p>
              <p className="text-green-700 mt-2">
                <strong>Giải thích:</strong> {currentTaskData.explanation}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Key Concept */}
      <div className="bg-gradient-to-r from-light-50 to-white p-6 rounded-2xl border border-light-200">
        <h4 className="font-bold text-dark-900 mb-3 flex items-center">
          <Info className="w-5 h-5 mr-2" />
          Khái niệm chính:
        </h4>
        <p className="text-dark-700 text-lg font-medium">
          {data.key_concept}
        </p>
      </div>
    </div>
  )
}

export default InteractiveVelocityExploration
